/**
 * Karten-Markdown → Preact-VNodes.
 *
 * Umfang wie im Original der YouTube-App (fcInlineFormat /
 * flashcardMarkdownToHtml): Überschriften, Trennlinie, Zitat, Aufzählung,
 * Absatz — inline dazu Verweise, ***fett-kursiv***, **fett**, *kursiv*,
 * ~~durchgestrichen~~, ==Hervorhebung==, `Code` und [c:#hex]Farbe[/c].
 * Dazugekommen: Bilder als ![Beschreibung](_bilder/datei.png).
 *
 * Unterschied zur Vorlage: es entsteht kein HTML-String. Kartentext kann
 * dadurch kein Markup einschleusen, und Zeichen in `Code` bleiben roh.
 */

// Reihenfolge zählt: längere Auszeichnungen zuerst, sonst frisst *kursiv*
// die Sterne von **fett**.
const INLINE_QUELLE = [
  // Vor dem Verweis: ohne das würde aus ![x](y) ein „!" plus Verweis.
  /!\[([^\]]*)\]\(([^)]+)\)/.source,
  /\[c:([#a-zA-Z0-9,.%()\s]+)\]([\s\S]*?)\[\/c\]/.source,
  /\[([^\]]+)\]\(([^)]+)\)/.source,
  /\*\*\*([\s\S]+?)\*\*\*/.source,
  /\*\*([\s\S]+?)\*\*/.source,
  /\*([^*\n]+?)\*/.source,
  /~~([^~]+)~~/.source,
  /==([^=]+)==/.source,
  /`([^`]+)`/.source,
].join('|')

const FARBE_ERLAUBT = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|(rgb|hsl)a?\([\d\s.,%]+\))$/

// Muss zu BILDER in server/cards/store.js passen.
const BILDER_ORDNER = '_bilder'

/**
 * Bildquelle einer Karte in die Ausspiel-Adresse, oder null.
 *
 * Erlaubt ist ausschließlich der eigene Bildordner. Eine fremde Adresse fällt
 * durch — nicht aus Prüderie, sondern weil die App hinter Tailscale läuft und
 * keinen Abruf nach außen auslösen soll. Ein Bild von Wikipedia lädt man einmal
 * herunter und legt es hier ab; danach ist es Teil der Sicherung.
 */
export function bildAdresse(quelle) {
  const pfad = String(quelle || '').trim()
  if (!pfad.startsWith(`${BILDER_ORDNER}/`)) return null
  const name = pfad.slice(BILDER_ORDNER.length + 1)
  if (!name || name.includes('/') || name.includes('\\') || name.includes('..')) return null
  return `/api/bilder/${encodeURIComponent(name)}`
}

function inline(text, tiefe = 0) {
  if (!text) return []
  if (tiefe > 8) return [text]

  const teile = []
  let zuletzt = 0
  // Eigenes Regex-Objekt je Aufruf: inline() ruft sich für den Inhalt jeder
  // Auszeichnung selbst auf, und ein geteiltes `g`-Regex würde dabei seinen
  // lastIndex verlieren — die äußere Schleife liefe endlos.
  const muster = new RegExp(INLINE_QUELLE, 'g')

  for (let treffer = muster.exec(text); treffer; treffer = muster.exec(text)) {
    if (treffer.index > zuletzt) teile.push(text.slice(zuletzt, treffer.index))

    const [
      , bildText, bildQuelle, farbe, farbInhalt, linkText, linkZiel,
      fettKursiv, fett, kursiv, weg, mark, code,
    ] = treffer
    const k = teile.length

    if (bildQuelle !== undefined) {
      const adresse = bildAdresse(bildQuelle)
      teile.push(
        adresse ? (
          <img key={k} class="md-bild" src={adresse} alt={bildText} loading="lazy" />
        ) : (
          // Sichtbar stehen lassen statt still verschlucken: sonst sucht man
          // beim Lernen nach einem Bild, das nie kommen konnte.
          <span key={k} class="md-bild-fehlt meta">Nur eigene Bilder: {bildQuelle}</span>
        ),
      )
    } else if (farbe !== undefined) {
      const sauber = farbe.trim()
      teile.push(
        FARBE_ERLAUBT.test(sauber) ? (
          <span key={k} style={{ color: sauber }}>{inline(farbInhalt, tiefe + 1)}</span>
        ) : (
          <span key={k}>{inline(farbInhalt, tiefe + 1)}</span>
        ),
      )
    } else if (linkText !== undefined) {
      teile.push(
        <a key={k} href={linkZiel} target="_blank" rel="noopener noreferrer">
          {inline(linkText, tiefe + 1)}
        </a>,
      )
    } else if (fettKursiv !== undefined) {
      teile.push(<strong key={k}><em>{inline(fettKursiv, tiefe + 1)}</em></strong>)
    } else if (fett !== undefined) {
      teile.push(<strong key={k}>{inline(fett, tiefe + 1)}</strong>)
    } else if (kursiv !== undefined) {
      teile.push(<em key={k}>{inline(kursiv, tiefe + 1)}</em>)
    } else if (weg !== undefined) {
      teile.push(<del key={k}>{inline(weg, tiefe + 1)}</del>)
    } else if (mark !== undefined) {
      teile.push(<mark key={k} class="md-mark">{inline(mark, tiefe + 1)}</mark>)
    } else if (code !== undefined) {
      // Roh: in Code wird nichts weiter ausgezeichnet.
      teile.push(<code key={k} class="md-code">{code}</code>)
    }

    zuletzt = treffer.index + treffer[0].length
  }

  if (zuletzt < text.length) teile.push(text.slice(zuletzt))
  return teile
}

/** Zeilen mit <br> dazwischen — im Original „\n → <br>" innerhalb eines Blocks. */
function mitUmbruechen(text, tiefe = 0) {
  const aus = []
  text.split('\n').forEach((zeile, i) => {
    if (i > 0) aus.push(<br key={`br${i}`} />)
    aus.push(...inline(zeile, tiefe))
  })
  return aus
}

function block(roh, i) {
  const t = roh.trim()
  if (!t) return null

  const einzeilig = !t.includes('\n')

  const ueberschrift = einzeilig ? t.match(/^(#{1,4}) (.+)$/) : null
  if (ueberschrift) {
    const Tag = `h${Math.min(4, ueberschrift[1].length)}`
    return <Tag key={i} class="md-h">{inline(ueberschrift[2])}</Tag>
  }

  if (einzeilig && t === '---') return <hr key={i} class="md-hr" />

  if (t.startsWith('>')) {
    const inhalt = t.split('\n').map((zeile) => zeile.replace(/^> ?/, '')).join('\n')
    return <blockquote key={i} class="md-bq">{mitUmbruechen(inhalt)}</blockquote>
  }

  const zeilen = t.split('\n')
  if (zeilen.every((zeile) => /^- /.test(zeile) || !zeile.trim())) {
    return (
      <ul key={i} class="md-ul">
        {zeilen
          .filter((zeile) => zeile.trim())
          .map((zeile, j) => <li key={j}>{inline(zeile.replace(/^- /, ''))}</li>)}
      </ul>
    )
  }

  return <p key={i} class="md-p">{mitUmbruechen(t)}</p>
}

/** Rendert Karten-Markdown. `text` ist immer Nutzerinhalt aus einer .md-Datei. */
export function KartenMarkdown({ text, class: klasse }) {
  const roh = String(text ?? '').replace(/\r\n/g, '\n')
  if (!roh.trim()) return null
  return <div class={klasse}>{roh.split(/\n\n+/).map(block).filter(Boolean)}</div>
}
